import React from 'react';
import cx from 'classnames';
import Tooltip from '../Tooltip';
import stylesheet from './index.css';
import { LOGO_MAP, isValidLogo } from './logo-map';
import { useColorScheme } from './../../helpers/utils-ts';

/**
 * Coin component can display any of the following items
 * - string: A string that represents the key of the image in the LOGO_MAP
 * - ReactElement: A React element that can be rendered directly - For icons
 * - 'mailchimp': A special case for the Mailchimp logo based on the dark/light color scheme
 */
type Image = string | React.ReactElement;

export type CoinProps = {
  images: Image[];
  fallbackImage?: Image;
};

const MAX_LOGOS_DISPLAYED = 4;

/**
 * Retrieves the configuration entry for a given integration key.
 *
 * @param {string} integration - The key representing the integration.
 * @returns {Object} The configuration object containing the label and image URL for the specified integration.
 */
const getIntegrationEntryConfig = (integration: string) => {
  return LOGO_MAP[integration];
};

/**
 * Constructs a comma-separated label string from a list of images,
 * starting from the index defined by MAX_LOGOS_DISPLAYED.
 * Each image is validated as a string and mapped to its corresponding
 * label using the getIntegrationEntryConfig function.
 *
 * @param {Image[]} images - An array of images, which can be either
 * an IntegrationKey or a React element.
 * @returns {string} A comma-separated string of labels for the images
 * beyond the MAX_LOGOS_DISPLAYED threshold.
 */
const getOverflowLabel = (images: Image[]) => {
  const labels = [];
  for (let i = MAX_LOGOS_DISPLAYED - 1; i < images.length; i++) {
    const image = images[i];
    if (image && typeof image === 'string' && isValidLogo(image)) {
      labels.push(getIntegrationEntryConfig(image).label);
    }
  }

  return labels.join(', ');
};

const CoinItem: React.FC<{
  image: Image;
  index: number;
  fallbackImage?: Image | null;
}> = (props) => {
  const { image, index, fallbackImage } = props;
  const colorScheme = useColorScheme();

  if (
    React.isValidElement(image) ||
    (typeof image === 'string' && isValidLogo(image))
  ) {
    // If the image is a string and valid logo, we are going to render it
    // by getting the logo from the LOGO_MAP
    const logo =
      typeof image === 'string' ? getIntegrationEntryConfig(image) : null;
    const label = `${logo?.label}`;
    const key = React.isValidElement(image) ? image.key : image;
    return (
      <li
        key={key}
        className={cx(stylesheet.coin)}
        style={{
          zIndex: MAX_LOGOS_DISPLAYED - index,
        }}
      >
        {React.isValidElement(image) ? (
          image
        ) : (
          <img
            src={
              colorScheme === 'dark'
                ? logo?.image.dark ?? logo?.image.light
                : logo?.image.light
            }
            alt={label}
          />
        )}
      </li>
    );
  }

  if (fallbackImage) {
    // If the image is not valid, we are going to render the fallback image
    return <CoinItem image={fallbackImage} index={index} />;
  }

  // Otherwise render nothing
  return null;
};

const Coin: React.FC<CoinProps> = ({
  images = [],
  fallbackImage = 'mailchimp',
}) => {
  const getCoinsToCreate = () => {
    if (images.length > MAX_LOGOS_DISPLAYED) {
      return images.slice(0, MAX_LOGOS_DISPLAYED - 1);
    }
    return images;
  };

  const coinsToCreate = getCoinsToCreate();
  const hasOverflow = images?.length > MAX_LOGOS_DISPLAYED;

  return (
    <ul className={stylesheet.coinContainer}>
      {coinsToCreate.map((image, index) => (
        <CoinItem
          key={typeof image === 'string' ? image : `icon_${index}`}
          image={image}
          index={index}
          fallbackImage={fallbackImage}
        />
      ))}
      {hasOverflow && (
        // Note: Tooltip offset seems to be out of place, needs fix in Tooltip component
        <Tooltip label={getOverflowLabel(images)}>
          <li className={cx(stylesheet.coin, stylesheet.overflow)}>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <span className={stylesheet.overflowText}>
              +{images.length - (MAX_LOGOS_DISPLAYED - 1)}
            </span>
          </li>
        </Tooltip>
      )}
    </ul>
  );
};

// This can useful utilities while consuming this component
export { isValidLogo } from './logo-map';

export default Coin;
